import type { OrderInfo } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import React, { useEffect, useState, useCallback } from 'react'
import { useCurrentCart } from './CartContext'
import useOrderProduct from '@@partner:helpers/hooks/useOrderProduct'
import { makeGraphQLErrorNotification } from '@@partner:helpers/notification'
import { formatPrice } from '@@partner:helpers/util'

import { Radio, RadioGroup } from 'rsuite'
import ProductSearchResult from '../product/search/ProductSearchResult'
import Cart from './Cart'
import ProductSearchInput from '../product/search/ProductSearchInput'

export interface OrderCartProps {
  order: OrderInfo
  onChange?: (valid: boolean) => void
  // onProceed: () => void
  children?: JSX.Element
}

export const TAB_SEARCH = 0
export const TAB_CART = 1

const OrderTabbedCart = ({ order, onChange, children }: OrderCartProps) => {
  const [ currentTab, setCurrentTab ] = useState(TAB_SEARCH)

  const {
    addToCurrentCart,
    deleteFromCurrentCart,
    setCurrentCartProducts,
    updateAmount,
    currentShopCart
  } = useCurrentCart()

  const { updateOrderProduct, deleteOrderProduct, loading } = useOrderProduct(
    order.id
  )

  useEffect(() => {
    if (order?.cartCompatibleCustomerProducts?.length > 0) {
      setCurrentCartProducts(order.cartCompatibleCustomerProducts)
    }
  }, [ order.cartCompatibleCustomerProducts, setCurrentCartProducts ])

  useEffect(() => {
    const cartTotalIsValid = (order.shop?.minOrderCost ?? 0) - order.total < 0
    const cartHasProducts = currentShopCart.products.length > 0

    const canProceed = cartTotalIsValid && cartHasProducts
    onChange?.(canProceed)
  }, [ currentShopCart, onChange, order ])

  const handleProductClick = (product_id: GQL_ID, amount: number) => {
    addToCurrentCart(product_id, amount)
    updateOrderProduct(product_id, amount)
      .then(({ data }) => {
        const total = data?.updated.updatedProduct.total ?? 0
        updateAmount(product_id, order.shop_id, amount, total)
      })
      .catch((error) => {
        // restore
        setCurrentCartProducts(order.cartCompatibleCustomerProducts)
        makeGraphQLErrorNotification(
          'Не удалось обновить количество выбранного товара'
        )(error)
      })
  }

  const handleProductDelete = (product_id: GQL_ID) => {
    deleteOrderProduct(product_id)
      .then(() => deleteFromCurrentCart(product_id))
      .catch(makeGraphQLErrorNotification('Не удалось удалить товар из списка'))
  }

  const handleChangeTab = useCallback((tab) => {
    setCurrentTab(tab)
  }, [])

  return (
    <div className="OrderTabbedCart">
      <div className="text-center">
        <RadioGroup
          className="my-2 inline-block"
          inline
          appearance="picker"
          value={currentTab}
          onChange={handleChangeTab}
        >
          <Radio className="OrderTabbedCart__Picker__Option" value={TAB_SEARCH}>
            Поиск
          </Radio>
          <Radio className="OrderTabbedCart__Picker__Option" value={TAB_CART}>
            Корзина ({currentShopCart.products.length} позиций на{' '}
            {formatPrice(order.total)})
          </Radio>
        </RadioGroup>
      </div>

      {currentTab === TAB_SEARCH && (
        <>
          <ProductSearchInput />
          <ProductSearchResult onProductClick={handleProductClick} />
        </>
      )}

      {currentTab === TAB_CART && (
        <Cart
          order={order}
          onDeleteProduct={handleProductDelete}
          onUpdateAmount={handleProductClick}
        />
      )}
    </div>
  )
}

export default OrderTabbedCart
